import GuideLayout from "../components/GuideLayout";
import { useLang } from "../i18n/LanguageContext";
import { faqPageLd } from "../lib/schema";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Private shore excursion and cruise transfer",
  name: "La Spezia Shore Excursion — Cinque Terre, Florence & Pisa",
  provider: { "@id": "https://yourblacklimo.com/#business" },
  areaServed: ["La Spezia", "Cinque Terre", "Portovenere", "Lerici", "Florence", "Pisa", "Lucca", "Tuscany"],
  description:
    "Private La Spezia shore excursions in a Mercedes V-Class with an English-speaking chauffeur — to the Cinque Terre, Portovenere, Florence, Pisa and Lucca. Pier-side pickup, ship tracking, guaranteed back-to-ship time.",
  url: "https://yourblacklimo.com/la-spezia-shore-excursion",
};

const CONTENT = {
  en: {
    seoTitle: "La Spezia Shore Excursion | Cinque Terre, Florence & Pisa Private Tour — Your Black Limo",
    seoDesc:
      "Private La Spezia shore excursion from the cruise port to the Cinque Terre, Portovenere, Florence, Pisa and Lucca. Mercedes V-Class, English-speaking driver, ship tracking and a guaranteed back-to-ship time.",
    overline: "La Spezia Shore Excursion",
    h1: "Private Shore Excursions from La Spezia Cruise Port",
    intro:
      "La Spezia is the gateway to the Cinque Terre and the Ligurian Riviera. Step off the ship into a Mercedes V-Class and spend your day ashore among the pastel villages, or head inland to Tuscany — with an English-speaking chauffeur and a guaranteed return to the pier.",
    heroAlt: "Private La Spezia shore excursion to the Cinque Terre in a black Mercedes V-Class",
    blocks: [
      {
        title: "The Cinque Terre & Ligurian coast",
        body: "From the La Spezia terminal we reach the coastal towns in minutes. We drive you to the villages and the panoramic viewpoints of Monterosso, Vernazza, Portovenere and Lerici, guiding you onto the scenic connections and waiting when you return — a relaxed day by the sea, entirely private.",
      },
      {
        title: "Inland to Tuscany",
        body: "Prefer art and cities? From La Spezia, Lucca is about an hour away, Pisa around 75 minutes and Florence roughly two hours — a full, comfortable day ashore with a private car and no coaches.",
      },
    ],
    cards: {
      overline: "Popular shore excursions",
      title: "Where your day from La Spezia could take you",
      items: [
        { title: "Cinque Terre", body: "Monterosso, Vernazza and the pastel villages clinging to the cliffs above a turquoise sea." },
        { title: "Portovenere & Lerici", body: "The elegant Gulf of Poets — colourful harbours, a seaside castle and quieter Ligurian charm." },
        { title: "Florence", body: "The Renaissance capital — the Duomo, Ponte Vecchio and Piazzale Michelangelo, at your own pace." },
        { title: "Pisa & Lucca", body: "The Leaning Tower and the Renaissance walls of Lucca — two Tuscan icons in one gentle day." },
      ],
    },
    note: {
      title: "How it works",
      body:
        "We track your ship's arrival and meet you pier-side with a name board. Every excursion is fully private — no shared coaches, no fixed groups. Your English-speaking chauffeur adapts the pace to you and guarantees a comfortable margin for your all-aboard time. Fully licensed NCC, professional drivers, and a Mercedes V-Class for up to seven guests. Entrance fees, boat and train tickets, meals and guided walking tours are booked and paid directly by the client.",
    },
    cta: {
      title: "Plan your shore excursion from La Spezia",
      secondaryLabel: "All shore excursions",
      secondaryHref: "/livorno-cruise-transfers",
      primaryLabel: "Request a quote",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Questions",
      title: "Frequently asked questions",
      items: [
        { q: "Can I visit the Cinque Terre from La Spezia in one day?", a: "Yes. La Spezia is the closest cruise port to the Cinque Terre. We drive you to the coast and onto the scenic train or boat connections between the villages, and are waiting when you return." },
        { q: "Can I go to Florence or Pisa from La Spezia instead?", a: "Absolutely. From La Spezia, Lucca is about an hour, Pisa around 75 minutes and Florence roughly two hours — all comfortable as a private day ashore." },
        { q: "Will I get back to my ship on time?", a: "Always. We track your ship's schedule, plan around your all-aboard time and build in a generous margin, so you return to the pier relaxed and never rushed." },
        { q: "Is the excursion private and with an English-speaking driver?", a: "Yes. Every excursion is fully private in a Mercedes V-Class with a professional, English-speaking chauffeur — never a shared group." },
      ],
    },
  },
  it: {
    seoTitle: "Escursione da La Spezia | Cinque Terre, Firenze e Pisa Tour Privato — Your Black Limo",
    seoDesc:
      "Escursione privata dal porto crociere di La Spezia verso Cinque Terre, Portovenere, Firenze, Pisa e Lucca. Mercedes Classe V, autista che parla inglese, monitoraggio nave e rientro garantito in orario.",
    overline: "Escursione da La Spezia",
    h1: "Escursioni Private a Terra dal Porto Crociere di La Spezia",
    intro:
      "La Spezia è la porta delle Cinque Terre e della Riviera ligure. Scendi dalla nave e sali su una Mercedes Classe V per trascorrere la tua giornata tra i borghi pastello, oppure verso l'interno in Toscana — con un autista che parla inglese e il rientro al molo garantito.",
    heroAlt: "Escursione privata da La Spezia alle Cinque Terre in una Mercedes Classe V nera",
    blocks: [
      {
        title: "Cinque Terre e costa ligure",
        body: "Dal terminal di La Spezia raggiungiamo i borghi costieri in pochi minuti. Ti accompagniamo ai paesi e ai punti panoramici di Monterosso, Vernazza, Portovenere e Lerici, indicandoti i collegamenti panoramici e attendendoti al ritorno — una giornata rilassata sul mare, completamente privata.",
      },
      {
        title: "Verso l'interno, in Toscana",
        body: "Preferisci arte e città? Da La Spezia, Lucca è a circa un'ora, Pisa a circa 75 minuti e Firenze a circa due ore — una giornata a terra piena e comoda con un'auto privata e senza pullman.",
      },
    ],
    cards: {
      overline: "Escursioni più richieste",
      title: "Dove può portarti la tua giornata da La Spezia",
      items: [
        { title: "Cinque Terre", body: "Monterosso, Vernazza e i borghi pastello aggrappati alle scogliere sopra un mare turchese." },
        { title: "Portovenere e Lerici", body: "L'elegante Golfo dei Poeti — porticcioli colorati, un castello sul mare e il fascino ligure più tranquillo." },
        { title: "Firenze", body: "La capitale del Rinascimento — il Duomo, Ponte Vecchio e Piazzale Michelangelo, al tuo ritmo." },
        { title: "Pisa e Lucca", body: "La Torre Pendente e le mura rinascimentali di Lucca — due icone toscane in una giornata tranquilla." },
      ],
    },
    note: {
      title: "Come funziona",
      body:
        "Monitoriamo l'arrivo della nave e ti accogliamo al molo con un cartello. Ogni escursione è completamente privata — niente pullman condivisi, niente gruppi fissi. Il tuo autista che parla inglese adatta il ritmo a te e garantisce un comodo margine sull'orario di imbarco. NCC regolarmente autorizzato, autisti professionisti e una Mercedes Classe V fino a sette ospiti. Biglietti d'ingresso, biglietti di barca e treno, pasti e visite guidate a piedi sono prenotati e pagati direttamente dal cliente.",
    },
    cta: {
      title: "Organizza la tua escursione da La Spezia",
      secondaryLabel: "Tutte le escursioni",
      secondaryHref: "/livorno-cruise-transfers",
      primaryLabel: "Richiedi un preventivo",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Domande",
      title: "Domande frequenti",
      items: [
        { q: "Posso visitare le Cinque Terre da La Spezia in un giorno?", a: "Sì. La Spezia è il porto crociere più vicino alle Cinque Terre. Ti accompagniamo sulla costa e sui collegamenti panoramici in treno o in barca tra i borghi, e ti aspettiamo al ritorno." },
        { q: "Posso andare a Firenze o Pisa da La Spezia?", a: "Certamente. Da La Spezia, Lucca è a circa un'ora, Pisa a circa 75 minuti e Firenze a circa due ore — tutte comode come giornata privata a terra." },
        { q: "Rientrerò alla nave in orario?", a: "Sempre. Monitoriamo gli orari della nave, pianifichiamo attorno all'orario di imbarco e prevediamo un ampio margine, così rientri al molo rilassato e senza fretta." },
        { q: "L'escursione è privata e con autista che parla inglese?", a: "Sì. Ogni escursione è completamente privata in una Mercedes Classe V con autista professionista che parla inglese — mai un gruppo condiviso." },
      ],
    },
  },
};

export default function LaSpeziaShoreExcursion() {
  const { lang } = useLang();
  const c = CONTENT[lang === "it" ? "it" : "en"];
  const { ["@context"]: _ctx, ...serviceNode } = jsonLd;
  const graphLd = { "@context": "https://schema.org", "@graph": [serviceNode, faqPageLd(c.faq.items)] };
  return (
    <GuideLayout
      testid="la-spezia-shore-guide"
      faq={c.faq}
      seo={{ title: c.seoTitle, description: c.seoDesc, path: "/la-spezia-shore-excursion", locale: lang === "it" ? "it_IT" : "en_US", jsonLd: graphLd }}
      overline={c.overline}
      h1={c.h1}
      intro={c.intro}
      heroImage={"https://static.prod-images.emergentagent.com/jobs/c98acbe0-6e23-474e-adcd-5ab166743bcb/images/05d0b5ea86560245a2eeac13598589ac1afd1a49b6e56fce6fbfe75639a90607.jpeg"}
      heroAlt={c.heroAlt}
      blocks={c.blocks}
      cards={c.cards}
      note={c.note}
      cta={c.cta}
    />
  );
}
